import { FC, useMemo } from 'react';
import { Calendar, XCircle, Bell, DollarSign, Info, CheckCheck, AlertCircle } from 'lucide-react';
import { cn } from '../../../utils/cn';
import {
  useTherapistNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from '../../../hooks/useTherapistDashboard';
import type { TherapistNotification } from '../../../types/therapist-dashboard.types';

/**
 * Get the appropriate icon component for a notification type
 */
function getNotificationIcon(type: TherapistNotification['type']) {
  const iconProps = { className: 'w-5 h-5' };

  switch (type) {
    case 'booking':
      return <Calendar {...iconProps} />;
    case 'cancellation':
      return <XCircle {...iconProps} />;
    case 'reminder':
      return <Bell {...iconProps} />;
    case 'payment':
      return <DollarSign {...iconProps} />;
    case 'system':
    default:
      return <Info {...iconProps} />;
  }
}

/**
 * Get the color classes for a notification type
 */
function getNotificationTypeStyles(type: TherapistNotification['type']) {
  switch (type) {
    case 'booking':
      return {
        bg: 'bg-blue-100 dark:bg-blue-900/30',
        icon: 'text-blue-600 dark:text-blue-400',
      };
    case 'cancellation':
      return {
        bg: 'bg-red-100 dark:bg-red-900/30',
        icon: 'text-red-600 dark:text-red-400',
      };
    case 'reminder':
      return {
        bg: 'bg-amber-100 dark:bg-amber-900/30',
        icon: 'text-amber-600 dark:text-amber-400',
      };
    case 'payment':
      return {
        bg: 'bg-green-100 dark:bg-green-900/30',
        icon: 'text-green-600 dark:text-green-400',
      };
    case 'system':
    default:
      return {
        bg: 'bg-gray-100 dark:bg-slate-700',
        icon: 'text-gray-600 dark:text-slate-400',
      };
  }
}

/**
 * Format a date string to relative time (e.g., "2 hours ago")
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  if (diffSeconds < 60) {
    return 'Just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  } else if (diffWeeks < 4) {
    return `${diffWeeks} week${diffWeeks === 1 ? '' : 's'} ago`;
  } else {
    return `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;
  }
}

interface NotificationItemProps {
  notification: TherapistNotification;
  onMarkRead: (id: string) => void;
  isMarking: boolean;
}

function NotificationItem({ notification, onMarkRead, isMarking }: NotificationItemProps) {
  const typeStyles = getNotificationTypeStyles(notification.type);

  return (
    <button
      type="button"
      onClick={() => !notification.read && onMarkRead(notification.id)}
      disabled={notification.read || isMarking}
      className={cn(
        'w-full text-left p-4 rounded-lg border transition-all',
        'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
        'dark:focus:ring-offset-slate-900',
        notification.read
          ? 'bg-white dark:bg-slate-800/50 border-gray-200 dark:border-slate-700 opacity-75'
          : 'bg-white dark:bg-slate-800 border-blue-200 dark:border-blue-800 hover:border-blue-300 dark:hover:border-blue-700 cursor-pointer shadow-sm',
        isMarking && 'opacity-50 cursor-wait'
      )}
      aria-label={
        notification.read
          ? `Read notification: ${notification.title}`
          : `Mark as read: ${notification.title}`
      }
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div
          className={cn('flex-shrink-0 p-2 rounded-full', typeStyles.bg)}
          aria-hidden="true"
        >
          <span className={typeStyles.icon}>{getNotificationIcon(notification.type)}</span>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3
              className={cn(
                'text-sm',
                notification.read
                  ? 'font-normal text-gray-700 dark:text-slate-300'
                  : 'font-semibold text-gray-900 dark:text-slate-100'
              )}
            >
              {notification.title}
            </h3>
            {!notification.read && (
              <span
                className="flex-shrink-0 w-2 h-2 mt-1.5 rounded-full bg-blue-500"
                aria-label="Unread"
              />
            )}
          </div>
          <p
            className={cn(
              'text-sm mt-0.5 line-clamp-2',
              notification.read
                ? 'text-gray-500 dark:text-slate-500'
                : 'text-gray-600 dark:text-slate-400'
            )}
          >
            {notification.message}
          </p>
          <div className="flex items-center gap-3 mt-2">
            <span className="text-xs text-gray-400 dark:text-slate-500">
              {formatRelativeTime(notification.createdAt)}
            </span>
            {notification.data?.clientName && (
              <span className="text-xs text-gray-500 dark:text-slate-400">
                Client: {notification.data.clientName}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

export const NotificationsSection: FC = () => {
  const { data: notifications, isLoading, error, refetch } = useTherapistNotifications();
  const markReadMutation = useMarkNotificationRead();
  const markAllReadMutation = useMarkAllNotificationsRead();

  // Calculate unread count
  const unreadCount = useMemo(() => {
    if (!notifications) return 0;
    return notifications.filter((n) => !n.read).length;
  }, [notifications]);

  // Handle marking single notification as read
  const handleMarkRead = (notificationId: string) => {
    markReadMutation.mutate(notificationId);
  };

  // Handle marking all notifications as read
  const handleMarkAllRead = () => {
    markAllReadMutation.mutate();
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Notifications</h2>
            <p className="text-gray-600 dark:text-slate-400 mt-1">Stay updated on your bookings</p>
          </div>
        </div>
        <div className="flex justify-center py-12">
          <div
            className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"
            role="status"
            aria-label="Loading notifications"
          >
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Notifications</h2>
            <p className="text-gray-600 dark:text-slate-400 mt-1">Stay updated on your bookings</p>
          </div>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-8 text-center border border-red-200 dark:border-red-800">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" aria-hidden="true" />
          <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
            Failed to Load Notifications
          </h3>
          <p className="text-red-600 dark:text-red-300 mb-4">{error.message}</p>
          <button
            onClick={() => refetch()}
            className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (!notifications || notifications.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Notifications</h2>
            <p className="text-gray-600 dark:text-slate-400 mt-1">Stay updated on your bookings</p>
          </div>
        </div>
        <div className="bg-gray-50 dark:bg-slate-800/50 rounded-lg p-12 text-center border border-gray-200 dark:border-slate-700">
          <Bell
            className="w-16 h-16 text-gray-300 dark:text-slate-600 mx-auto mb-4"
            aria-hidden="true"
          />
          <h3 className="text-lg font-semibold text-gray-700 dark:text-slate-300 mb-2">
            No Notifications
          </h3>
          <p className="text-gray-500 dark:text-slate-400 max-w-sm mx-auto">
            You're all caught up! New notifications about bookings, payments, and reminders will
            appear here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Notifications</h2>
            {unreadCount > 0 && (
              <span
                className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200"
                aria-label={`${unreadCount} unread notifications`}
              >
                {unreadCount} unread
              </span>
            )}
          </div>
          <p className="text-gray-600 dark:text-slate-400 mt-1">Stay updated on your bookings</p>
        </div>

        {/* Mark all as read button */}
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            disabled={markAllReadMutation.isPending}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium',
              'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
              'hover:bg-blue-100 dark:hover:bg-blue-900/50',
              'border border-blue-200 dark:border-blue-800',
              'transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
              'dark:focus:ring-offset-slate-900',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {markAllReadMutation.isPending ? (
              <>
                <div
                  className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"
                  role="status"
                  aria-hidden="true"
                />
                <span>Marking...</span>
              </>
            ) : (
              <>
                <CheckCheck className="w-4 h-4" aria-hidden="true" />
                <span>Mark all as read</span>
              </>
            )}
          </button>
        )}
      </div>

      {/* Notification List */}
      <div className="space-y-3" role="list" aria-label="Notifications">
        {notifications.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onMarkRead={handleMarkRead}
            isMarking={markReadMutation.isPending}
          />
        ))}
      </div>

      {/* Summary footer */}
      <div className="bg-gray-50 dark:bg-slate-800/50 rounded-lg p-4 border border-gray-200 dark:border-slate-700">
        <div className="flex flex-wrap items-center justify-between gap-4 text-sm">
          <div className="text-gray-600 dark:text-slate-400">
            Showing {notifications.length} notification{notifications.length === 1 ? '' : 's'}
          </div>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-blue-500" aria-hidden="true" />
              <span className="text-gray-600 dark:text-slate-400">{unreadCount} unread</span>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-gray-300 dark:bg-slate-600" aria-hidden="true" />
              <span className="text-gray-600 dark:text-slate-400">
                {notifications.length - unreadCount} read
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
